<script setup lang="ts">
import { AtomIcon, AtomButton } from "@chatfood/design-system";
import { bemBuilder, toMoney } from "@chatfood/core-utils";
import { computed } from "vue";
import { useCustomerStore } from "~/stores/customer";
import { useLoyaltyProgramStore } from "~/stores/loyaltyProgram";

const css = bemBuilder("loyalty-balance-button");

const props = withDefaults(
  defineProps<{
    viewOnly?: boolean;
  }>(),
  {
    viewOnly: false,
  },
);

const emits = defineEmits<{
  click: [event: MouseEvent];
}>();

const customer = useCustomerStore();
const loyaltyProgram = useLoyaltyProgramStore();

const showCash = computed(() => {
  const pointsBalance = customer.loyalty?.wallet?.points?.balance ?? 0;
  const cashBalance = customer.loyalty?.wallet?.cash?.balance.value ?? 0;

  // Priority order: Points > Cash > Points (if can redeem) > Cash (default)
  if (pointsBalance > 0) {
    // Show points when user has points
    return false;
  } else if (cashBalance > 0) {
    // Show cash when user has cash but no points
    return true;
  } else if (loyaltyProgram.canRedeemPoints) {
    // Show points when user has no balance but can redeem points
    return false;
  } else {
    return true;
  }
});
</script>

<template>
  <div
    :class="[
      css(''),
      {
        [css('', 'view-only')]: props.viewOnly,
      },
    ]"
  >
    <AtomButton
      :class="css('cta')"
      theme="ghost"
      variant="primary"
      @click="emits('click', $event)"
    >
      <AtomIcon
        :name="showCash ? 'dollar-sign' : 'rating-ghost'"
        ignore-color
      />
      <span>{{
        showCash
          ? toMoney(
              customer.loyalty.wallet.cash.balance.value,
              customer.loyalty.wallet.cash.balance.currency,
            )
          : customer.loyalty.wallet.points.balance
      }}</span>
    </AtomButton>
  </div>
</template>

<style lang="scss" scoped>
.loyalty-balance-button {
  $self: &;

  &__cta {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: var(--spacing-sm);
  }

  &--view-only {
    #{$self}__cta {
      cursor: default;
      pointer-events: none;
    }
  }
}
</style>
